import axios from "axios";
import { env } from "@/config/env";
import { CLIENT_SUFFIX_HEADER, getClientSuffix } from "@/config/tenant";
import { ACCESS_COOKIE, SESSION_COOKIE } from "@/config/cookies";
import { TIMEOUT_MS } from "@/config/timeouts";
import { ensureSessionId, readServerSessionId } from "@/shared/http/session-id";
import { isTokenExpiring } from "@/shared/http/jwt";
import { isNestEnvelope } from "@/shared/http/nest-envelope";
import { notifySessionExpired } from "./session-expired";

/**
 * Client das rotas PRIVADAS e de toda MUTAÇÃO. Manda identidade: session id
 * sempre, e no SSR o Bearer do cookie httpOnly.
 *
 * ── Como escolher entre os dois clients ────────────────────────────────────
 *   privateHttp  →  precisa saber QUEM está pedindo (perfil, pedidos,
 *                   favoritos, carrinho) ou ESCREVE algo. Nunca cacheável.
 *   publicGet    →  a resposta é a mesma para qualquer visitante (catálogo,
 *                   categoria, widget, settings). Anônimo e cacheável.
 * Errar para o lado privado só custa cache; errar para o lado público vaza
 * estado de um cliente no HTML compartilhado — ver `public-http.ts`.
 *
 * Os services chamam sempre paths relativos (ex.: privateHttp.get("/product/item"))
 * e recebem o `data` já desembrulhado. A autenticação é 100% custodiada pelo
 * servidor:
 *
 * - No browser, o baseURL é o proxy same-origin /api/nest. O JS NÃO toca em
 *   token nenhum: os cookies httpOnly vão junto e o proxy anexa/renova o
 *   access. Por isso um reload não dispara refresh — o gargalo sumiu.
 * - No SSR, chama a nest direto e anexa o access token lido do cookie
 *   httpOnly (via next/headers), renderizando já autenticado quando o token
 *   está válido.
 */
export const privateHttp = axios.create({
    baseURL: `${env.apiUrl}/api/v1`,
    timeout: TIMEOUT_MS.httpClient,
});

/** SSR: lê o access token do cookie httpOnly, se houver e ainda válido. */
async function serverAccessToken(): Promise<string | null> {
    try {
        const { cookies } = await import("next/headers");
        const token = (await cookies()).get(ACCESS_COOKIE)?.value;
        // Não anexa token já expirado: pouparia um 401 certo. Se expirou, o
        // SSR renderiza como convidado e o browser corrige via proxy.
        if (token && !isTokenExpiring(token, 5)) return token;
        return null;
    } catch {
        return null;
    }
}

privateHttp.interceptors.request.use(async (config) => {
    // A própria rota de criação de sessão não precisa (nem pode esperar) do id.
    if (!config.url?.includes("/auth/session/")) {
        if (typeof window === "undefined") {
            // SSR: só o id do cookie do request. Sem cookie válido, o header é
            // omitido — a nest recusa qualquer id que não tenha emitido, então
            // inventar um aqui era um 400 garantido.
            const serverId = await readServerSessionId();
            if (serverId) config.headers.set(SESSION_COOKIE, serverId);
        } else {
            config.headers.set(SESSION_COOKIE, await ensureSessionId());
        }
    }

    if (typeof window === "undefined") {
        // SSR → nest direto, com o access token do cookie (quando válido) e o
        // tenant do request. O suffix vem do header que o Kong injetou, nunca
        // de env: é o que permite o mesmo processo servir clientes diferentes.
        config.baseURL = `${env.apiUrl}/api/v1`;
        config.headers.set(CLIENT_SUFFIX_HEADER, await getClientSuffix());
        const token = await serverAccessToken();
        if (token) config.headers.set("authorization", `Bearer ${token}`);
    } else {
        // Browser → proxy same-origin. Cookies httpOnly seguem sozinhos; o
        // proxy anexa e renova o token E carimba o x-client-suffix a partir do
        // header do request. O JS não conhece nem manda o tenant — se mandasse,
        // seria um valor forjável e o proxy o descartaria de qualquer forma.
        config.baseURL = "/api/nest";
    }

    return config;
});

privateHttp.interceptors.response.use(
    (response) => {
        if (isNestEnvelope(response.data)) {
            response.data = response.data.data;
        }
        return response;
    },
    (error) => {
        // O proxy sinaliza sessão de fato expirada (refresh falhou) com o header
        // x-auth-expired — só então deslogamos a UI. Um 401 comum (autorização
        // de um endpoint) NÃO derruba a sessão.
        if (typeof window !== "undefined" && error.response?.headers?.["x-auth-expired"] === "1") {
            notifySessionExpired();
        }

        return Promise.reject(error);
    }
);
